import type { QueryClient } from '@tanstack/react-query'

import { getSession } from '@/features/auth/session-store'
import { websocketUrl } from '@/shared/config/runtime'
import { isUnrecoverableAuth, planReconnect, RECONNECT_JITTER } from './backoff'
import { getConnection } from './connection-store'
import {
  FIRST_CONNECT_CURSOR,
  NO_REPLAY_AVAILABLE,
  nextCursor,
  parseMessage,
  type PageSubscription,
  type RealtimeMessage,
} from './protocol'

/** What a handler is given. The query cache is the only thing they patch. */
export interface RealtimeContext {
  queryClient: QueryClient
  /** Ends the session here without ending it at the identity provider. */
  endSession: () => void
}

export type RealtimeHandler = (context: RealtimeContext, data: RealtimeMessage) => void

/**
 * The part of `WebSocket` this client uses.
 *
 * Structural rather than the DOM type so a spec can drive the client with a fake
 * socket it opens and closes by hand, which is the only way to test a reconnect
 * without a server.
 */
export interface RealtimeSocket {
  readyState: number
  send: (data: string) => void
  close: () => void
  onopen: ((event: Event) => void) | null
  onmessage: ((event: MessageEvent) => void) | null
  onclose: ((event: CloseEvent) => void) | null
  onerror: ((event: Event) => void) | null
}

export const SOCKET_CONNECTING = 0
export const SOCKET_OPEN = 1

export interface RealtimeClientOptions {
  /** Defaults to the global `WebSocket`. */
  createSocket?: (url: string) => RealtimeSocket
  /** Defaults to `Math.random`. Injected so backoff delays are exact in specs. */
  random?: () => number
}

export interface RealtimeClient {
  connect: (options?: { reconnect?: boolean; anonymous?: boolean }) => void
  disconnect: () => void
  /** Supplies what handlers patch. Set by the React provider once it has a cache. */
  setContext: (context: RealtimeContext | null) => void
  subscribe: (page: string | null, parameters: PageSubscription['parameters']) => void
  unsubscribe: (page: string | null, parameters: PageSubscription['parameters']) => void
  /** Registers a handler and returns the function that removes it. */
  on: (type: string, handler: RealtimeHandler) => () => void
  /** For specs and for the connection banner's "retry now". */
  readonly state: () => {
    connected: boolean
    attempts: number
    cursor: number
    replayEnabled: boolean
    pages: readonly PageSubscription[]
  }
}

/**
 * `navigator.onLine === false` is the only part of that API worth trusting.
 * `true` means "there is an interface up", which is routinely true with no
 * route to anywhere, so it is not evidence of connectivity -- but `false` is
 * reliable evidence of its absence.
 */
function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

function isDocumentVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
}

function sameSubscription(a: PageSubscription, b: PageSubscription): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function createRealtimeClient(
  options: RealtimeClientOptions = {},
): RealtimeClient {
  const createSocket = options.createSocket ?? ((url: string) => new WebSocket(url))
  const random = options.random ?? Math.random

  let socket: RealtimeSocket | null = null
  let context: RealtimeContext | null = null
  let connected = false
  let reconnect = false
  let anonymous = false
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let attempts = 0
  let pages: PageSubscription[] = []
  let subscribedToPages = true
  let lastToken: string | null = null
  let authenticationSuccess = true
  let authResponseReceived = false
  let unloading = false
  let cursor: number = FIRST_CONNECT_CURSOR
  let replayEnabled = false
  let listenersAttached = false

  const handlers = new Map<string, Set<RealtimeHandler>>()

  function send(payload: unknown): void {
    socket?.send(JSON.stringify(payload))
  }

  function dispatch(message: RealtimeMessage): void {
    const registered = handlers.get(message.type)
    if (registered === undefined || context === null) return
    // Copied first: a handler that removes itself -- the common shape for a
    // one-shot -- would otherwise mutate the set being iterated.
    for (const handler of [...registered]) handler(context, message)
  }

  // --- lifecycle listeners -------------------------------------------------
  //
  // Attached while the client is meant to be holding a connection, so a client
  // that was never connected leaves nothing behind on window.

  const onPageHide = (): void => {
    unloading = true
  }

  /**
   * Retry now, from any signal that means the page is alive again.
   *
   * Clearing the unload latch here is the point. A `pagehide` followed by a
   * `pageshow` is a bfcache restore rather than a real unload, and without this
   * the latch set on the way out would survive the return and suppress every
   * reconnect for the rest of the page's life.
   */
  const retryNow = (): void => {
    unloading = false
    if (!connected && reconnect) {
      if (reconnectTimeout !== null) clearTimeout(reconnectTimeout)
      reconnectTimeout = null
      attempts = 0
      getConnection().setFailed(false)
      // The reconnecting banner deliberately stays up until the socket opens.
      // Flickering it off here shows a connected-looking interface for the
      // length of the round trip that has not happened yet.
      openSocket()
    }
  }

  const onVisibilityChange = (): void => {
    if (!isDocumentVisible()) return
    retryNow()
  }

  function attachListeners(): void {
    if (listenersAttached || typeof window === 'undefined') return
    window.addEventListener('beforeunload', onPageHide)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', retryNow)
    window.addEventListener('online', retryNow)
    document.addEventListener('visibilitychange', onVisibilityChange)
    listenersAttached = true
  }

  function detachListeners(): void {
    if (!listenersAttached || typeof window === 'undefined') return
    window.removeEventListener('beforeunload', onPageHide)
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('pageshow', retryNow)
    window.removeEventListener('online', retryNow)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    listenersAttached = false
  }

  // --- connecting ----------------------------------------------------------

  function openSocket(): void {
    const session = getSession()
    const jwt = session.accessToken
    const token = anonymous ? (jwt ?? 'anonymous') : jwt

    if (
      socket !== null &&
      (socket.readyState === SOCKET_CONNECTING || socket.readyState === SOCKET_OPEN)
    ) {
      return
    }

    if (socket !== null) {
      // Detached first: closing a socket whose `onclose` still points here would
      // schedule a reconnect for the connection being replaced.
      socket.onclose = null
      socket = null
    }

    if (
      isUnrecoverableAuth({
        token,
        lastToken,
        authResponseReceived,
        authenticationSuccess,
      })
    ) {
      // A hidden tab gets no banner. It would be read on return as describing
      // the present, when what it describes is a token that has since been
      // refreshed by the timer.
      if (!isDocumentHidden()) getConnection().setFailed(true)
      getConnection().setReconnecting(false)
      return
    }

    lastToken = token
    authResponseReceived = false

    const url = `${websocketUrl()}?jwt_token=${String(token)}&web_socket_id=${session.webSocketId}`
    const next = createSocket(url)
    socket = next

    next.onopen = () => {
      connected = true
      attempts = 0
      authenticationSuccess = true

      const connection = getConnection()
      connection.setConnected(true)
      connection.setFailed(false)
      connection.setReconnecting(false)

      if (!subscribedToPages) subscribeToPages()
    }

    next.onmessage = (event: MessageEvent) => {
      const message = parseMessage(event.data)
      if (message === null) return

      // Before dispatch, and unconditionally. The cursor records what this
      // client has *seen*, not what it handled: an event with no handler still
      // happened, and a handler that throws must not be able to rewind the
      // replay position for every other handler.
      cursor = nextCursor(cursor, message)

      handleProtocolMessage(message)
      dispatch(message)
    }

    next.onclose = () => {
      connected = false
      getConnection().setConnected(false)
      // Nothing subscribed means nothing outstanding to restore, so the next
      // open has no resubscription round to do. With pages, the flag goes false
      // and `onopen` replays them.
      subscribedToPages = pages.length === 0
      scheduleReconnect()
    }
  }

  function scheduleReconnect(): void {
    const connection = getConnection()
    const plan = planReconnect({
      reconnect,
      unloading,
      documentHidden: isDocumentHidden(),
      online: isOnline(),
      attempts,
      jitter: Math.floor(random() * RECONNECT_JITTER),
    })

    switch (plan.action) {
      case 'stop':
      case 'await-visible':
        return
      case 'await-online':
        connection.setReconnecting(true)
        return
      case 'give-up':
        attempts += 1
        connection.setReconnecting(false)
        connection.setFailed(true)
        return
      case 'schedule':
        if (reconnectTimeout !== null) clearTimeout(reconnectTimeout)
        attempts = plan.attempt
        connection.setReconnecting(true)
        reconnectTimeout = setTimeout(openSocket, plan.delay)
    }
  }

  // --- replay --------------------------------------------------------------

  function handleProtocolMessage(message: RealtimeMessage): void {
    if (message.type === 'authentication') {
      authenticationSuccess = message.success === true
      authResponseReceived = true
      replayEnabled = message.replay_enabled === true

      // Not merely "nothing to replay": the server is not recording, so no
      // future cursor of ours can mean anything either.
      if (!replayEnabled) cursor = NO_REPLAY_AVAILABLE

      if (authenticationSuccess && replayEnabled && socket?.readyState === SOCKET_OPEN) {
        send({ type: 'replay_events', last_seen_id: cursor })
      }
      return
    }

    if (message.type === 'replay_events_result') {
      const latest = message.latest_event_id
      const forceRefresh = message.force_refresh === true
      if (!forceRefresh && typeof latest === 'number') {
        // Never moved backwards: the replayed events themselves have already
        // advanced the cursor past `latest` by the time this arrives.
        cursor = Math.max(latest, cursor)
      }
      getConnection().setWorkspaceOutdated(forceRefresh)
      return
    }

    if (message.type === 'force_disconnect') {
      // Stop before ending the session, or the close this provokes is read as a
      // drop and answered with a reconnect.
      reconnect = false
      context?.endSession()
    }
  }

  // --- pages ---------------------------------------------------------------

  function subscribeToPage(subscription: PageSubscription): void {
    if (!connected) return
    send({ page: subscription.page ?? '', ...subscription.parameters })
  }

  function subscribeToPages(): void {
    if (subscribedToPages) return
    for (const subscription of pages) subscribeToPage(subscription)
    subscribedToPages = true
  }

  // --- public surface ------------------------------------------------------

  return {
    connect: (connectOptions = {}) => {
      reconnect = connectOptions.reconnect ?? true
      anonymous = connectOptions.anonymous ?? false
      attachListeners()
      openSocket()
    },

    disconnect: () => {
      if (socket !== null) {
        socket.onclose = null
        socket.close()
        socket = null
      }

      detachListeners()
      if (reconnectTimeout !== null) clearTimeout(reconnectTimeout)
      reconnectTimeout = null
      reconnect = false
      attempts = 0
      connected = false
      unloading = false
      cursor = FIRST_CONNECT_CURSOR
      replayEnabled = false
      getConnection().reset()
    },

    setContext: (next) => {
      context = next
    },

    subscribe: (page, parameters) => {
      const subscription: PageSubscription = { page, parameters }
      if (pages.some((existing) => sameSubscription(existing, subscription))) return

      pages.push(subscription)
      if (connected) {
        subscribeToPage(subscription)
      } else {
        subscribedToPages = false
      }
    },

    unsubscribe: (page, parameters) => {
      const subscription: PageSubscription = { page, parameters }
      pages = pages.filter((existing) => !sameSubscription(existing, subscription))
      if (connected) send({ remove_page: page, ...parameters })
    },

    on: (type, handler) => {
      const existing = handlers.get(type)
      if (existing === undefined) {
        handlers.set(type, new Set([handler]))
      } else {
        existing.add(handler)
      }
      return () => {
        handlers.get(type)?.delete(handler)
      }
    },

    state: () => ({
      connected,
      attempts,
      cursor,
      replayEnabled,
      pages: [...pages],
    }),
  }
}
